import { Button, Form, Input, Popconfirm, Select, message } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import AssessmentMarksTable from "./AssessmentMarksTable";
import { serverUrl } from "../../../../nestserver";
import { numberInputGuard } from "../../../../components/inputs/numberInputGuard";
import { ASSESSMENT_TYPES, labelsFor } from "./assessmentType";

function EditAssessment({
  id,
  onBack,
  classesUrl = "/teachers/classes",
  type = ASSESSMENT_TYPES.exam,
}) {
  const labels = labelsFor(type);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const [editable, setEditable] = useState(false);
  const { Option } = Select;

  const serverReasonOf = (error) => {
    const reason = error?.response?.data?.message;
    return Array.isArray(reason) ? reason[0] : reason;
  };

  const onFinish = (values) => {
    axios({
      method: "patch",
      url: serverUrl + "/exam-v2/" + id,
      // class and subject are fixed after creation, so only these are sent.
      data: {
        name: values.name,
        total_marks: Number(values.total_marks),
      },
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    })
      .then(() => {
        message.success(`${labels.one} Updated`);
        queryClient.invalidateQueries("assessments");
        queryClient.invalidateQueries(["assessments", id]);
        setEditable(false);
      })
      .catch((e) => {
        message.error(serverReasonOf(e) || e.message);
      });
  };

  function deleteAssessment() {
    axios({
      method: "delete",
      url: serverUrl + "/exam-v2/" + id,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    })
      .then(() => {
        message.success(`${labels.one} Deleted`);
        queryClient.invalidateQueries("assessments");
        queryClient.invalidateQueries(["assessments", id]);
        onBack();
      })
      .catch((e) => {
        message.error(serverReasonOf(e) || e.message);
      });
  }

  // fetch assessment
  const fetchAssessment = async () => {
    const res = await axios.get(serverUrl + "/exam-v2/" + id, {
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const assessment = useQuery(["assessments", id], fetchAssessment, {
    enabled: !!id,
  });

  // fetch Class
  const fetchClasses = async () => {
    const res = await axios({
      method: "get",
      url: serverUrl + classesUrl,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const classes = useQuery(["classes", classesUrl], fetchClasses);

  async function fetchSubjects() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/subjects/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

    return res.data;
  }
  const subjects = useQuery("subjects", fetchSubjects);

  const classId = assessment.data?.exam?.class?._id;

  useEffect(() => {
    form.resetFields();
  }, [assessment.data, editable, form]);

  return (
    <>
      <div className="py-8">
        <div className="bg-white p-8 rounded-2xl">
          <div className="pb-4">
            <div className="flex items-center justify-end">
              <Button onClick={onBack}>Back</Button>
            </div>
          </div>
          <div className="flex items-center justify-between py-5">
            <h1 className="text-2xl text-primary capitalize">
              {assessment.isSuccess && assessment.data?.exam?.name}
            </h1>
            <div className="flex flex-row items-center space-x-4">
              <Popconfirm
                placement="bottom"
                title="Are you sure ?"
                onConfirm={deleteAssessment}
                okText="Yes"
                cancelText="No"
              >
                <Button type="danger">Delete</Button>
              </Popconfirm>

              {!editable ? (
                <Button onClick={() => setEditable(true)} type="success">
                  Edit
                </Button>
              ) : (
                <Button onClick={() => form.submit()} type="success">
                  Save
                </Button>
              )}
            </div>
          </div>

          <div>
            <Form
              form={form}
              layout="vertical"
              name="basic"
              onFinish={onFinish}
              autoComplete="off"
              initialValues={{
                ...assessment.data?.exam,
                class: assessment?.data?.exam?.class?._id,
                subject: assessment?.data?.exam?.subject?._id,
              }}
            >
              <Form.Item
                label={`${labels.one} Name`}
                name="name"
                rules={[
                  {
                    required: true,
                    message: `Please input ${labels.one} Name !`,
                  },
                ]}
              >
                <Input
                  disabled={!editable}
                  maxLength={130}
                  showCount={true}
                  className="w-1/2 py-2"
                />
              </Form.Item>

              <div className="flex flex-row space-x-5 w-full">
                <Form.Item label="Select Class" name="class">
                  <Select
                    disabled={true}
                    showSearch
                    showArrow
                    placeholder="Select Class"
                    className="w-full bg-gray-300"
                    style={{
                      border: "6px",
                      backgroundColor: "#EBEBEB",
                    }}
                  >
                    {classes.isSuccess &&
                      classes?.data?.map((data) => {
                        return (
                          <Option key={data._id} value={data._id}>
                            {data.name}
                          </Option>
                        );
                      })}
                  </Select>
                </Form.Item>
                {classId && (
                  <Form.Item label="Subject" name="subject">
                    <Select
                      disabled={true}
                      showSearch
                      showArrow
                      placeholder="Select Subject"
                      style={{
                        border: "6px",
                        backgroundColor: "#EBEBEB",
                      }}
                      className="w-3/4"
                    >
                      {subjects.isSuccess &&
                        subjects?.data
                          ?.filter((s) => s.class?._id === classId)
                          ?.map((data) => {
                            return (
                              <Option key={data._id} value={data._id}>
                                {data.name}
                              </Option>
                            );
                          })}
                    </Select>
                  </Form.Item>
                )}
              </div>
              <div className="flex flex-row space-x-16 pt-5">
                <Form.Item
                  label="Total Marks"
                  name="total_marks"
                  rules={[
                    {
                      required: true,
                      message: "Please input Total Marks !",
                    },
                    {
                      pattern: /^[1-9]\d*$/,
                      message: "Please enter a positive number",
                    },
                  ]}
                >
                  <Input
                    type="number"
                    min={1}
                    disabled={!editable}
                    className="w-36"
                    placeholder="20"
                    {...numberInputGuard}
                  />
                </Form.Item>
              </div>
            </Form>
          </div>
        </div>

        {id && (
          <AssessmentMarksTable
            assessmentId={id}
            subjectId={assessment.data?.exam?.subject?._id}
            assessmentData={assessment.data}
          />
        )}
      </div>
    </>
  );
}

export default EditAssessment;
